<script lang="ts" setup>
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';
import useMediaStore from '../../stores/media';
import useUiStore from '../../stores/ui';
import useReaderStore from '../../stores/reader';
import useAudioTracks from '../../composables/use-audio-tracks';
import LanguagesCard from './LanguagesCard.vue';
import SideDrawer from '../../components/SideDrawer.vue';
import { sendBookReaderEventAnalytics } from '../../composables/use-send-analytics';

type Emits = {
  (e: 'selectTextLanguage', languageCode: string): void;
  (e: 'update:model-value', value: boolean): void;
  (e: 'selectAudioLanguage', languageCode: string): void;
};
const emit = defineEmits<Emits>();

type Props = {
  showTooltips?: boolean;
  activator?: any;
};

withDefaults(defineProps<Props>(), {
  showTooltips: true,
});

const { t } = useLocale();
const { smAndDown } = useDisplay();
const mediaStore = useMediaStore();
const {
  audioLanguages,
  videoLanguages,
  audioLanguage,
  otherTextLanguagesCodes,
  mainLanguage,
  secondaryLanguage,
  hasMediaAvailable,
} = storeToRefs(mediaStore);
const { setPreferredLanguage, setFirstTrackInPageAsCurrent } = mediaStore;
const { setCurrentTracks } = useAudioTracks();
const uiStore = useUiStore();
const { isLanguageMenuVisible } = storeToRefs(uiStore);
const { toggleLanguageMenu } = uiStore;
const { isFrogBook, isAudioOnly } = storeToRefs(useReaderStore());

type LanguageItem = {
  title: string;
  value: string;
};

const languageCodeToListItem = (languageCode: string): LanguageItem => ({
  title: t(`${languageCode}_long`),
  value: languageCode,
});

const translatedLanguageSort = (languageItemA: LanguageItem, languageItemB: LanguageItem) => {
  return languageItemA.title < languageItemB.title ? -1 : 1;
};

const availableLanguages = computed(() => [...audioLanguages.value, ...videoLanguages.value]);

const textLanguagesItems = computed(() =>
  otherTextLanguagesCodes.value.map(languageCodeToListItem).sort(translatedLanguageSort),
);

const audioLanguagesItems = computed(() =>
  availableLanguages.value.map(languageCodeToListItem).sort(translatedLanguageSort),
);

const onSelectAudioLanguage = async (language: string) => {
  console.debug(`onSelectAudioLanguage`, language);
  setPreferredLanguage(language);

  emit('selectAudioLanguage', language);

  //Handle audio language switch in parent component when on audio only.
  if (isAudioOnly.value) return;

  await setCurrentTracks();
  setFirstTrackInPageAsCurrent();
  toggleLanguageMenu(false);
  sendBookReaderEventAnalytics('AUDIO_LANGUAGE_SELECTED');
};

const onSelectTextLanguage = async (languageCode: string) => {
  console.debug(`onSelectTextLanguage`, languageCode);
  emit('selectTextLanguage', languageCode);
  toggleLanguageMenu(false);
  sendBookReaderEventAnalytics('TEXT_LANGUAGE_SELECTED');
};

const isAudioSwitchingAvailable = computed(
  () => hasMediaAvailable.value && availableLanguages.value && availableLanguages.value.length > 1,
);

const isTextSwitchingAvailable = computed(() => !!otherTextLanguagesCodes.value?.length);

const isLanguageSwitcherAvailable = computed(
  () => !isFrogBook.value && (isAudioSwitchingAvailable.value || isTextSwitchingAvailable.value),
);

watch(isLanguageMenuVisible, (value) => {
  emit('update:model-value', value);
});

defineExpose({
  isLanguageSwitcherAvailable,
  isAudioSwitchingAvailable,
});
</script>

<template>
  <v-menu
    v-if="!smAndDown && isLanguageSwitcherAvailable && !isAudioOnly"
    id="language-list"
    v-model="isLanguageMenuVisible"
    :activator="activator"
    :close-on-content-click="false"
    location="top"
    close-delay="500"
  >
    <LanguagesCard :showTooltips="showTooltips" @close="toggleLanguageMenu(false)">
      <slot
        name="text-languages"
        :items="textLanguagesItems"
        :mainLanguage="mainLanguage"
        :secondaryLanguage="secondaryLanguage"
        :isAvailable="isTextSwitchingAvailable"
        :onSelect="onSelectTextLanguage"
      />
      <slot
        name="audio-languages"
        :items="audioLanguagesItems"
        :audioLanguage="audioLanguage"
        :isAvailable="isAudioSwitchingAvailable"
        :onSelect="onSelectAudioLanguage"
      />
    </LanguagesCard>
  </v-menu>
  <div v-else-if="!smAndDown && isLanguageSwitcherAvailable && isAudioOnly" class="d-flex mt-1 bg-transparent">
    <SideDrawer
      v-model="isLanguageMenuVisible"
      location="right"
      :title="t('label_language')"
      icon="fa-regular fa-globe"
    >
      <slot
        name="audio-languages"
        :items="audioLanguagesItems"
        :audioLanguage="audioLanguage"
        :isAvailable="isAudioSwitchingAvailable"
        :onSelect="onSelectAudioLanguage"
      />
    </SideDrawer>
  </div>
  <v-bottom-sheet
    v-else-if="isLanguageSwitcherAvailable"
    id="language-list"
    v-model="isLanguageMenuVisible"
    :activator="activator"
    :close-on-content-click="false"
    close-delay="500"
  >
    <LanguagesCard :showTooltips="showTooltips" @close="toggleLanguageMenu(false)">
      <slot
        name="text-languages"
        :items="textLanguagesItems"
        :mainLanguage="mainLanguage"
        :secondaryLanguage="secondaryLanguage"
        :isAvailable="isTextSwitchingAvailable"
        :onSelect="onSelectTextLanguage"
      />
      <slot
        name="audio-languages"
        :items="audioLanguagesItems"
        :audioLanguage="audioLanguage"
        :isAvailable="isAudioSwitchingAvailable"
        :onSelect="onSelectAudioLanguage"
      />
    </LanguagesCard>
  </v-bottom-sheet>
</template>

<style lang="scss" scoped></style>
